import {
  Cell,
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { first, isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'hotel/navigation';

import RosterFilterSidebar from 'components/RosterTable/RosterFilterSidebar';

import {
  RosterRequestsFilterInput,
  RosterRequestsQuery,
  RosterRequestStatus,
  RosterRoomAssignment,
  RosterRoomAssignmentStatus,
  useRosterRequestsQuery,
} from '../../generated/graphql';
import { useTravelerSelfCheck } from '../../hooks/useTravelerSelfCheck';
import { useAppSelector } from '../../store/store';
import buildUserDisplayName from '../../utilities/buildUserDisplayName';
import getEnumLabel from '../../utilities/getEnumLabel';
import { getControlFieldLabelForPod } from '../../utilities/podBasedFiltering';
import { popToast } from '../../utilities/popToast';
import useQueryUrl from '../../utilities/useQueryUrl';
import useQueryUrlFilters from '../../utilities/useQueryUrlFilters';
import useQueryUrlSorting from '../../utilities/useQueryUrlSorting';
import { RequestStatusBadge } from '../Badge';
import { Button, PrimaryButtonLink } from '../Button';
import Table from '../Table';
import TablePaginationFooter from '../TablePaginationFooter';
import TablePaginationHeader from '../TablePaginationHeader';

import FiltersRequestChipContainer from './FiltersRequestChipContainer';
import RequestFilterForm from './RequestFilterForm';

export type TableRosterRequest =
  RosterRequestsQuery['rosterRequests']['items'][0];

const columnHelper = createColumnHelper<TableRosterRequest>();

const paginatedDefaults = {
  pageIndex: 0,
  pageSize: 25,
};

const RequestsTable: FC = () => {
  const { rosterRequestType, rosterRequestGroups } = useAppSelector(
    (state) => state.enums,
  );
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);

  // Check if client is inactive
  const client = useAppSelector((state) => state.user.profile?.client);
  const isClientInactive = client?.isInactive ?? false;

  const { isOnlyTraveler, hasLinkedProfile } = useTravelerSelfCheck();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const filterDefault = useQueryUrlFilters<RosterRequestsFilterInput>(
    {
      status: [
        RosterRequestStatus.AwaitingConfirmation,
        RosterRequestStatus.InProgress,
        RosterRequestStatus.MoreInfoRequired,
        RosterRequestStatus.RequestReceived,
        RosterRequestStatus.OverPdApprovalNeeded,
      ],
    },
    undefined,
    undefined,
  );

  const sortingDefaultValues = [
    {
      id: 'status',
      desc: false,
    },
  ];

  const sortingDefault = useQueryUrlSorting(sortingDefaultValues);

  const [filters, setFilters] = useState<RosterRequestsFilterInput | undefined>(
    filterDefault,
  );

  const [sorting, setSorting] = useState<SortingState | undefined>(
    sortingDefault,
  );

  const [pagination, setPagination] = useState(paginatedDefaults);

  useQueryUrl(first(sorting), filters);

  const {
    data,
    loading: loadingRoster,
    fetchMore,
    refetch,
  } = useRosterRequestsQuery({
    variables: {
      offset: paginatedDefaults.pageIndex,
      limit: paginatedDefaults.pageSize,
      sort: first(sortingDefault),
      filter: filterDefault,
    },
    notifyOnNetworkStatusChange: true,
    onError(error) {
      popToast(`Error loading roster: ${error.message}`, { type: 'error' });
    },
  });

  useEffect(() => {
    fetchMore({
      variables: {
        offset: pagination.pageSize * pagination.pageIndex,
        limit: pagination.pageSize,
        sort: first(sorting),
        filter: filters,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        return fetchMoreResult;
      },
    });
  }, [pagination.pageIndex, pagination.pageSize, sorting, filters, fetchMore]);

  const resetPagination = useCallback(() => {
    setPagination({
      ...paginatedDefaults,
      pageSize: pagination.pageSize,
    });
  }, [pagination.pageSize]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('status', {
        header: 'STATUS',
        cell: (info) => <RequestStatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('id', {
        header: 'ID',
        cell: (info) => (
          <Link
            to={`/requests/${info.getValue()}/`}
            className="font-semibold text-secondaryBlue underline"
          >
            {info.getValue() ?? '-'}
          </Link>
        ),
      }),
      columnHelper.accessor('controlNumber', {
        header: getControlFieldLabelForPod(clientPod),
        cell: (info) => info.getValue() ?? '-',
      }),
      columnHelper.accessor('requestType', {
        header: 'REQUEST TYPE',
        cell: (info) => (
          <>{getEnumLabel(rosterRequestType, info.getValue()) ?? '-'}</>
        ),
      }),
      columnHelper.accessor('assignedGroup', {
        header: 'GROUP',
        cell: (info) => {
          const group = info.getValue();

          return group ? getEnumLabel(rosterRequestGroups, group) : '-';
        },
      }),
      columnHelper.accessor('requestedBy', {
        header: 'REQUESTED BY',
        cell: (info) => {
          const user = info.getValue();
          return buildUserDisplayName(user);
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'ADDED AT',
        cell: (info) => {
          const formatted = format(
            parseISO(info.getValue()),
            'M/d/yyyy h:mm aa',
          );
          return <>{formatted}</>;
        },
      }),
    ],
    [rosterRequestType, rosterRequestGroups, clientPod],
  );

  const tableInstance = useReactTable({
    data: data?.rosterRequests?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    // pagination
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount: data?.rosterRequests?.total,
    // sorting
    manualSorting: true,
    onSortingChange: (sortingUpdater) => {
      resetPagination();
      // eslint-disable-next-line
      // @ts-ignore
      const newSortVal = sortingUpdater(sorting);

      // we check here if the internal table sorting state is empty
      // if so we fallback to our default sort values
      // setting defaults here prevents useQueryUrlSorting from causing an extra query without sort values
      if (isEmpty(newSortVal)) {
        setSorting(sortingDefaultValues);
      } else {
        setSorting(sortingUpdater as SortingState);
      }
    },
    enableMultiSort: false,
    // filtering
    manualFiltering: true,
    // table state
    state: {
      sorting,
      pagination,
      columnPinning: {
        left: ['guest', 'guest_semiUniqueIdentifier'],
      },
    },
    meta: {
      getCellClass(
        cell: Cell<RosterRoomAssignment, unknown>,
      ): string | undefined {
        const isStatusColumn = cell.column.id === 'status';
        const { status } = cell.row.original;

        if (status === RosterRoomAssignmentStatus.Upcoming) {
          return isStatusColumn ? 'bg-blue-100' : 'bg-blue-50';
        }
        if (status === RosterRoomAssignmentStatus.Past) {
          return isStatusColumn ? 'bg-yellow-100' : 'bg-yellow-50';
        }
        if (status === RosterRoomAssignmentStatus.InHouse) {
          return isStatusColumn ? 'bg-green-100' : 'bg-green-50';
        }
      },
    },
  });

  const onSubmit = async (values: RosterRequestsFilterInput) => {
    resetPagination();
    const { status: statusArray } = values;

    // ignore empty arrays
    const status =
      statusArray && statusArray.length > 0 ? statusArray : undefined;

    const valuesToSet = {
      ...values,
      status,
    };

    setFilters(isEmpty(valuesToSet) ? undefined : valuesToSet);
  };

  const handleRefresh = useCallback(async () => {
    await refetch({
      offset: pagination.pageSize * pagination.pageIndex,
      limit: pagination.pageSize,
      sort: first(sorting),
      filter: filters,
    });

    popToast('Table has been refreshed with the latest changes.');
  }, [filters, pagination.pageIndex, pagination.pageSize, refetch, sorting]);

  return (
    <div>
      <div className="flex flex-col ">
        <FiltersRequestChipContainer
          filters={filters ?? {}}
          onUpdateFilter={setFilters}
          onSubmit={onSubmit}
          openSidebar={() => setIsSidebarOpen(true)}
        />
        <RosterFilterSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        >
          <RequestFilterForm
            onSubmit={onSubmit}
            initialFilters={filterDefault}
            onClose={() => setIsSidebarOpen(false)}
          />
        </RosterFilterSidebar>
        <TablePaginationHeader
          tableInstance={tableInstance}
          action={
            <div className="hidden sm:flex sm:items-center sm:gap-2">
              <Button text="Refresh" onClick={handleRefresh} />
              {/* Pure travelers without linked profiles cannot create reservations */}
              {(!isOnlyTraveler || hasLinkedProfile) && (
                <PrimaryButtonLink
                  text="New Reservation"
                  to="/requests/new"
                  disabled={isClientInactive}
                />
              )}
            </div>
          }
        />
        <Table
          tableInstance={tableInstance}
          loading={loadingRoster}
          isRequest={true}
        />
        <TablePaginationFooter tableInstance={tableInstance} />
      </div>
    </div>
  );
};

export default RequestsTable;
